// TelcoTrack — D5 occupancy-schema export blockers (do not mix numbering layers).

import {
  cabinetCassetteRackForType,
  getRozvadecCabinetType,
  resolveRozvadecCabinetTypeFromMaterials,
} from './rozvadec-cabinet.constants';
import {
  getRozvadecCassetteType,
  isValidRozvadecKm5LcLabel,
} from './rozvadec-cassette.constants';
import {
  occupancyCabinetCodeFromTypeId,
} from './rozvadec-occupancy-schema.ids';
import type {
  RozvadecOccupancyBlocker,
  RozvadecOccupancyBlockerCode,
  RozvadecOccupancyGenerateInput,
  RozvadecOccupancySchema,
} from './rozvadec-occupancy-schema.types';
import {
  isCompleteWeldIncomingRef,
  rozvadecWeldIncomingRefKey,
  rozvadecWeldTargetKey,
} from './rozvadec-weld-layout.ids';
import { isRozvadecCassetteBoundDirect } from './rozvadec-weld-layout.pairs';
import type { RozvadecWeldLayout } from './rozvadec-weld-layout.types';

function blocker(
  code: RozvadecOccupancyBlockerCode,
  path: string,
  message: string,
): RozvadecOccupancyBlocker {
  return { code, path, message };
}

export function validateRozvadecOccupancySchema(
  schema: RozvadecOccupancySchema,
  layout: RozvadecWeldLayout,
  input: RozvadecOccupancyGenerateInput = {},
): RozvadecOccupancyBlocker[] {
  const blockers: RozvadecOccupancyBlocker[] = [];
  const typeId = input.typeId ?? schema.typeId;
  const type = getRozvadecCabinetType(typeId);
  const cabinetCode = occupancyCabinetCodeFromTypeId(typeId);
  const rack = cabinetCassetteRackForType(typeId);

  if (cabinetCode === 'MUK24') {
    if (schema.rows.length !== 1 || schema.rows[0]?.positions !== 24) {
      blockers.push(
        blocker('MUK24_ROW_INVALID', 'rows', 'MUK24 must be exactly one independent row of positions 1–24.'),
      );
    }
  }
  if (cabinetCode === 'MUK48') {
    const ok =
      schema.rows.length === 2 &&
      schema.rows.every((row) => row.positions === 24) &&
      schema.rows[0]?.id === 'rada_1' &&
      schema.rows[1]?.id === 'rada_2';
    if (!ok) {
      blockers.push(
        blocker(
          'MUK48_ROWS_INVALID',
          'rows',
          'MUK48 must be two independent rows of positions 1–24 (never a single 1–48 series).',
        ),
      );
    }
  }

  const resolved = resolveRozvadecCabinetTypeFromMaterials(input.materials ?? []);
  if (resolved.status === 'resolved' && typeId && resolved.type.id !== typeId) {
    blockers.push(
      blocker(
        'SAP_TYPE_CONFLICT',
        'cabinet.sap',
        `SAP identity ${resolved.matches.map((match) => match.sapCode).join(',')} conflicts with type ${typeId}.`,
      ),
    );
  } else if (type && input.derivedFromSap && type.identitySapCodes.length > 0) {
    const sap = input.derivedFromSap.trim();
    if (sap && !type.identitySapCodes.includes(sap)) {
      blockers.push(blocker('SAP_TYPE_CONFLICT', 'cabinet.sap', `SAP ${sap} does not identify ${type.id}.`));
    }
  }

  if (cabinetCode === 'RAYTR3') {
    if (schema.rendererId !== 'D5-RAYTR3') {
      blockers.push(blocker('RENDERER_UNSUPPORTED', 'rendererId', 'RAY-TR3 requires D5-RAYTR3.'));
    }
    return blockers;
  }

  const occupiedKeys = new Set<string>();
  schema.graph.cassettes.forEach((cassette, index) => {
    const def = getRozvadecCassetteType(cassette.cassetteType);
    const path = `graph.cassettes[${index}]`;
    if (def?.span === 2 && cassette.startPosition === 24) {
      blockers.push(blocker('KM5_START_ON_24', `${path}.startPosition`, 'KM5 must not start on cabinet position 24.'));
    }
    const expected = def?.span === 2
      ? [cassette.startPosition, cassette.startPosition + 1]
      : [cassette.startPosition];
    if (
      cassette.displayPosition !== cassette.startPosition ||
      cassette.occupiedPositions.join(',') !== expected.join(',')
    ) {
      blockers.push(
        blocker(
          'KM5_OVERLAP',
          path,
          'KM5 occupancy must be display_position = start and occupied = [start, start+1].',
        ),
      );
    }
    for (const position of cassette.occupiedPositions) {
      const key = `${cassette.rowId}#${position}`;
      if (occupiedKeys.has(key)) {
        blockers.push(blocker('KM5_OVERLAP', path, 'Cassette overlaps another cassette.'));
      }
      occupiedKeys.add(key);
    }
  });

  schema.graph.connectors.forEach((connector, index) => {
    const cassette = schema.graph.cassettes.find((item) => item.id === connector.cassetteId);
    const def = cassette ? getRozvadecCassetteType(cassette.cassetteType) : undefined;
    const path = `graph.connectors[${index}]`;
    if (def?.adapterFamily === 'SC' && (connector.adapterPosition > 12 || connector.polarity != null)) {
      blockers.push(blocker('KM5_SC_PORT_EXCEEDED', path, 'KM5/SC allows at most 12 SC ports (SC-1…SC-12).'));
    }
    if (def?.adapterFamily === 'LC') {
      if (connector.adapterPosition > 12) {
        blockers.push(
          blocker('KM5_LC_PORT_EXCEEDED', path, 'KM5/LC allows at most 12 duplex adapters (24 fibers).'),
        );
      }
      if (!isValidRozvadecKm5LcLabel(connector.printLabel)) {
        blockers.push(
          blocker('KM5_LC_LABEL_INVALID', `${path}.printLabel`, 'KM5/LC print id must be LC-1a…LC-12b.'),
        );
      }
    }
  });

  const lcFiberCountByCassette = new Map<string, number>();
  const scFiberCountByCassette = new Map<string, number>();
  for (const connector of schema.graph.connectors) {
    const cassette = schema.graph.cassettes.find((item) => item.id === connector.cassetteId);
    if (cassette?.adapterFamily === 'LC') {
      lcFiberCountByCassette.set(cassette.id, (lcFiberCountByCassette.get(cassette.id) ?? 0) + 1);
    }
    if (cassette?.adapterFamily === 'SC') {
      scFiberCountByCassette.set(cassette.id, (scFiberCountByCassette.get(cassette.id) ?? 0) + 1);
    }
  }
  for (const [cassetteId, count] of lcFiberCountByCassette) {
    if (count > 24) {
      blockers.push(
        blocker('KM5_LC_PORT_EXCEEDED', `cassettes.${cassetteId}`, 'KM5/LC exceeds 24 fibers.'),
      );
    }
  }
  for (const [cassetteId, count] of scFiberCountByCassette) {
    if (count > 12) {
      blockers.push(
        blocker('KM5_SC_PORT_EXCEEDED', `cassettes.${cassetteId}`, 'KM5/SC exceeds 12 ports.'),
      );
    }
  }

  schema.graph.splitters.forEach((splitter, index) => {
    const path = `graph.splitters[${index}]`;
    if (splitter.printLabel.startsWith('SPL-') === false && splitter.ratio === '1:16' && splitter.insideCassette) {
      // still validate shape
    }
    if (splitter.ratio === '1:16' && splitter.insideCassette) {
      if (splitter.inputs.length !== 1 || splitter.outputs.length !== 16) {
        blockers.push(
          blocker('SPLITTER_1X16_SHAPE_INVALID', path, '1:16 splitter must have IN 1/1 and OUT 1/1…1/16.'),
        );
      }
    }
    if (splitter.ratio === '1:8' && splitter.insideCassette) {
      const siblings = schema.graph.splitters.filter((item) => item.cassetteId === splitter.cassetteId);
      if (siblings.length !== 2 || splitter.inputs.length !== 1 || splitter.outputs.length !== 8) {
        blockers.push(
          blocker(
            'SPLITTER_2X8_SHAPE_INVALID',
            path,
            '2×1:8 must be two distinct splitters, each with its own IN and OUT 1…8.',
          ),
        );
      }
    }
    if (splitter.ratio === '1:4' && splitter.insideCassette) {
      const siblings = schema.graph.splitters.filter((item) => item.cassetteId === splitter.cassetteId);
      if (siblings.length === 1) {
        if (splitter.inputs.length !== 1 || splitter.outputs.length !== 4) {
          blockers.push(
            blocker('SPLITTER_1X4_SHAPE_INVALID', path, '1:4 splitter must have IN 1/1 and OUT 1/1…1/4.'),
          );
        }
      } else if (siblings.length !== 2 || splitter.inputs.length !== 1 || splitter.outputs.length !== 4) {
        blockers.push(
          blocker(
            'SPLITTER_2X4_SHAPE_INVALID',
            path,
            '2×1:4 must be two distinct splitters, each with its own IN and OUT 1…4.',
          ),
        );
      }
    }
    if (cabinetCode === 'PON32' && splitter.insideCassette) {
      blockers.push(
        blocker('PON32_SPLITTER_INSIDE_KM6', path, 'PON32 splitter body must stay outside KM6.'),
      );
    }
  });

  const dropByOutput = new Map<string, number>();
  for (const assignment of schema.graph.assignments) {
    if (assignment.via === 'splitter' && assignment.splitterPortId) {
      dropByOutput.set(
        assignment.splitterPortId,
        (dropByOutput.get(assignment.splitterPortId) ?? 0) + 1,
      );
    }
  }
  for (const [portId, count] of dropByOutput) {
    if (count > 1) {
      blockers.push(
        blocker('SPLITTER_OUTPUT_MULTI_DROP', `ports.${portId}`, 'One splitter output is mapped to multiple drops.'),
      );
    }
  }

  if (cabinetCode === 'PON32') {
    const available = schema.pon32?.availableKm6Count ?? rack?.sections[0]?.positions ?? 0;
    const km6Count = schema.graph.cassettes.filter((item) => item.cassetteType === 'KM6').length;
    const weldSeats = schema.graph.splices.filter((item) => item.kind === 'tray').length;
    if (available > 0 && (km6Count > available || weldSeats > available * 24)) {
      blockers.push(
        blocker(
          'PON32_WELD_EXCEEDS_KM6',
          'pon32',
          'Weld count exceeds available KM6 trays for this hardware revision / connector kit.',
        ),
      );
    }
    schema.graph.splitters.forEach((splitter, index) => {
      for (const output of splitter.outputs) {
        if (!output.target) continue;
        const hasSplice = schema.graph.splices.some(
          (splice) =>
            splice.kind === 'tray' &&
            splice.fibers.some(
              (fiber) =>
                output.incoming &&
                fiber.cableId === output.incoming.cableId &&
                fiber.physicalFiber === output.incoming.physicalFiber,
            ),
        );
        const dropSplice = schema.graph.assignments.some(
          (assignment) =>
            assignment.splitterPortId === output.id && assignment.spliceId != null,
        );
        if (!hasSplice && !dropSplice) {
          blockers.push(
            blocker(
              'PON32_OUTPUT_MISSING_KM6_SPLICE',
              `graph.splitters[${index}]`,
              'PON32 splitter output is missing a KM6 splice.',
            ),
          );
        }
      }
    });
  }

  const connectorKeys = new Map<string, number>();
  schema.graph.connectors.forEach((connector, index) => {
    const key = `${connector.cassetteId}#${connector.adapterPosition}#${connector.polarity ?? ''}`;
    connectorKeys.set(key, (connectorKeys.get(key) ?? 0) + 1);
    if ((connectorKeys.get(key) ?? 0) > 1) {
      blockers.push(
        blocker('CONNECTOR_DOUBLE_OCCUPIED', `graph.connectors[${index}]`, 'Two fibers share the same connector.'),
      );
    }
  });
  const spliceKeys = new Map<string, number>();
  schema.graph.splices.forEach((splice, index) => {
    const key = `${splice.cassetteId}#${splice.seat}`;
    spliceKeys.set(key, (spliceKeys.get(key) ?? 0) + 1);
    if ((spliceKeys.get(key) ?? 0) > 1) {
      blockers.push(
        blocker('SPLICE_DOUBLE_OCCUPIED', `graph.splices[${index}]`, 'Two fibers share the same splice seat.'),
      );
    }
    if (splice.kind === 'pigtail' && splice.fibers.length > 1) {
      blockers.push(
        blocker(
          'CONNECTOR_DOUBLE_OCCUPIED',
          `graph.splices[${index}]`,
          'A connector rear pigtail can hold only one cable fiber.',
        ),
      );
    }
  });

  const numbers = input.fiberNumbers ?? [];
  const numberKey = (cableId: string, physical: number) => `${cableId.trim()}#${physical}`;
  const numberByKey = new Map(numbers.map((row) => [numberKey(row.cableId, row.physicalFiber), row]));

  const checkFiber = (cableId: string, physical: number | null, path: string) => {
    if (!cableId || physical == null || physical < 1) {
      blockers.push(blocker('FIBER_NUMBER_MISSING', path, 'Missing physical fiber number.'));
      return;
    }
    const row = numberByKey.get(numberKey(cableId, physical));
    if (numbers.length > 0 && (!row || row.logicalFiber == null)) {
      blockers.push(blocker('FIBER_NUMBER_MISSING', path, 'Missing logical fiber number.'));
    }
  };

  schema.graph.fibers.forEach((fiber, index) => {
    checkFiber(fiber.cableId, fiber.physicalFiber, `graph.fibers[${index}]`);
  });

  schema.graph.splitters.forEach((splitter, index) => {
    for (const inputPort of splitter.inputs) {
      if (inputPort.target) {
        blockers.push(
          blocker(
            'ADDRESS_ON_SPLITTER_INPUT',
            `graph.splitters[${index}].inputs`,
            'Address must sit on a splitter output, not the input.',
          ),
        );
      }
    }
  });

  const reserveIncoming = new Set<string>();
  for (const link of layout.links) {
    if (!isRozvadecCassetteBoundDirect(link) || link.target) continue;
    if (isCompleteWeldIncomingRef(link.incoming)) {
      reserveIncoming.add(rozvadecWeldIncomingRefKey(link.incoming));
    }
  }
  for (const assignment of schema.graph.assignments) {
    const key = `${assignment.cableId}#${assignment.physicalFiber}`;
    const incomingKey = layout.links
      .filter((link) => link.target && rozvadecWeldTargetKey(link.target) === `${assignment.buildingId}#${assignment.cableId}#${assignment.physicalFiber}`)
      .map((link) => (link.incoming ? rozvadecWeldIncomingRefKey(link.incoming) : ''))
      .find(Boolean);
    if (incomingKey && reserveIncoming.has(incomingKey)) {
      blockers.push(
        blocker(
          'RESERVE_MARKED_IN_SERVICE',
          `assignments.${assignment.id}`,
          'Reserve fiber is marked as in-service.',
        ),
      );
    }
    void key;
  }

  if (schema.networkRole === 'SDF') {
    for (const splitter of schema.graph.splitters) {
      if (splitter.ratio === '1:4') {
        blockers.push(
          blocker(
            'SDF_1X4_NOT_USED',
            `graph.splitters.${splitter.id}`,
            'Běžné SDF v MUK 1:4 nepoužívá — agregace 1:4 patří jen na aSDF.',
          ),
        );
      }
    }
  }

  const supported =
    schema.rendererId === 'D5-MUK' ||
    schema.rendererId === 'D5-PON32' ||
    schema.rendererId === 'D5-RAYTR3';
  if (
    !supported &&
    (cabinetCode === 'MUK24' || cabinetCode === 'MUK48' || cabinetCode === 'PON32')
  ) {
    blockers.push(blocker('RENDERER_UNSUPPORTED', 'rendererId', 'Cabinet type has no D5 renderer in this pass.'));
  }
  if (!supported) {
    blockers.push(blocker('RENDERER_UNSUPPORTED', 'rendererId', 'Renderer is stubbed and cannot export D5.'));
  }

  return blockers;
}

export function occupancySchemaExportable(blockers: readonly RozvadecOccupancyBlocker[]): boolean {
  return blockers.length === 0;
}
